import React, { useState } from "react";
import * as PropTypes from "prop-types";

import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import SaveAlt from "@material-ui/icons/SaveAlt";

import { downloadTargetsOutputPEPFARXLSX } from "../../../api/server_calls";

import { RS } from "../../../data/strings/global";
import * as SC from "../../../data/strings/PIStringConst";

import * as Theme from "../../../app/Theme";

import TComboBox from "../../common/TComboBox";
import TButton from "../../common/TButton";
import SuperTableShim from "../../common/SuperTableShim";
import { generateTypes } from "../../../utilities";

import * as gbtu from "../../GB/GBTableUtil";
import * as pias from "../NonComponents/PIAppState";
import * as piasu from "../NonComponents/PIAppStateUtil";
import * as pisc from "../NonComponents/PIServerConst";
import * as pitu from "../NonComponents/PITableUtil";
import * as piu from "../NonComponents/PIUtil";
import * as pip from "../NonComponents/PIProps";
import * as pic from "../NonComponents/PIConst";
import * as pieh from "../NonComponents/PIEventHandlers";

import PITargDisagDistrictPriorPopsAggResTable from "../Tables/Results/PITargDisagDistrictPriorPopsAggResTable";
import PITargDisagDistrictPriorPopsResTable from "../Tables/Results/PITargDisagDistrictPriorPopsResTable";

import PIMethodComboBox, { PIMethodComboBoxProps } from "../Other/PIMethodComboBox";
import PITimePeriodDiv, { PITimePeriodDivProps } from "../Other/PITimePeriodDiv";
import PITimePeriodLab, { PITimePeriodLabProps } from "../Other/PITimePeriodLab";

/**
 * @typedef {import("../NonComponents/PIUtil").AvenirDateObject} AvenirDateObject
 */

/**
 * Returns ag-grid cellRenderer containing target population download button
 * @param {func} onDownload - Handler to call when download button clicked
 * @returns {func} cellRenderer - AG Grid cell renderer component
 */
function DownloadRendererFn(onDownload, disabled) {
  return (props) => (
    <Box style={{ display: "flex", justifyContent: "center" }}>
      <IconButton
        key={"downloadBtn"}
        onClick={() => onDownload(props.rowIndex)}
        size="small"
        color={"secondary"}
        disabled={disabled}
      >
        <SaveAlt />
      </IconButton>
    </Box>
  );
}

const PIPepfarSessionDownload = ({ modVarObjList, currentFinancialYear, aggregateFilenames, disabled }) => {
  function handleDownloadSession(rowIndex) {
    // sessions are in index order other than skipping first fixed col
    downloadTargetsOutputPEPFARXLSX(modVarObjList, rowIndex - 1);
  }

  function getPackTable() {
    let pt = gbtu.getNewPackTable();
    pt = gbtu.resizePackTable(pt, aggregateFilenames.length + 1, 2);

    // Header
    gbtu.setValue(pt, 0, 0, "Session");
    gbtu.setValue(pt, 0, 1, "");

    // Data
    for (const [idx, filename] of aggregateFilenames.entries()) {
      gbtu.setValue(pt, idx + 1, 0, filename);
      gbtu.setComponent(pt, idx + 1, 1, "download-button");
    }

    gbtu.setRowHeights(pt, 35);
    gbtu.setColWidth(pt, 1, 45);
    gbtu.setFixedCols(pt, 0);
    gbtu.lockCol(pt, 1, true, false);

    return pt;
  }

  const title = RS(SC.GB_stDownloadPepfarTargets)
    .replace("FY24", `FY${currentFinancialYear % 100}`)
    .replace("2024", currentFinancialYear.toString());

  const packTable = getPackTable();

  return (
    <>
      <p
        style={{
          marginTop: Theme.topIndent,
          marginBottom: Theme.ctrlSpacing,
          ...Theme.labelStyle,
        }}
      >
        {title}
      </p>
      <SuperTableShim
        font={Theme.tableFont}
        headerBackgroundColor={Theme.PI_PrimaryColor}
        oddRowBackgroundColor={Theme.PI_BandColor}
        readOnly={true}
        packTable={packTable}
        types={generateTypes(packTable, ["s", "cm"])}
        customComponents={{
          "download-button": DownloadRendererFn(handleDownloadSession, disabled),
        }}
        removedMenuNames={pitu.tableHideMenuItems}
        style={{
          fontFamily: Theme.tableFont,
          marginTop: Theme.ctrlSpacing,
          padding: 0,
        }}
      />
    </>
  );
};

const PIDisagTargetsResultContent = (props) => {
  const modVarObjList = props.modVarObjList;
  const onModVarsChange = props.onModVarsChange;

  const PrEPStatus = piasu.getModVarValue(modVarObjList, pisc.selectedPrEPStatusMVTag);
  const methods = piasu.getModVarValue(modVarObjList, pisc.methodsMVTag);
  const area2Name = piasu.getModVarValue(modVarObjList, pisc.disagTargArea2NameMVTag);
  const isAggMode = piasu.getModVarValue(modVarObjList, pisc.appModeMVTag) === pisc.aggregateToolMstID;
  const levelNames = piasu.getModVarValue(modVarObjList, pisc.adminSubnatLevelsDisagMVTag, isAggMode);
  // Level name isn't currently used in agg mode. It should be accessed as explicit "list" if needed later.
  const level1Name = isAggMode ? "" : levelNames[0];
  const countryCode = piasu.getModVarValue(modVarObjList, pisc.countryISOMVTag);
  const showDisagTable = !(isAggMode && countryCode === "multiple");

  /** @type {string[]} */
  const aggregateFilenames = piasu.getModVarValue(modVarObjList, pisc.aggFileNamesMVTag, true);

  const targSettingPeriodObj = piasu.getTargSettingPeriodObj(modVarObjList);
  const dateRangeDisplayObj = piasu.getDateRangeDisplayObj(modVarObjList);
  const [drdState, setDRDState] = useState(dateRangeDisplayObj);

  const currentFinancialYear = piu.getCurrentFinancialYear();
  const includesFinancialYear = piu.includesFinalYear(drdState, currentFinancialYear);

  const onPrEPStatusComboBoxChange = (value, text, info) => {
    const modVarObjListClone = structuredClone(modVarObjList);
    piasu.setModVarValue(modVarObjListClone, pisc.selectedPrEPStatusMVTag, info);
    onModVarsChange(modVarObjListClone, false);
  };

  const onDateRangeDisplayChange = (monthOrYearInt, startOrEndInt, successFn) => {
    const newDRD = structuredClone(drdState);
    piasu.setTimeframeData(newDRD, monthOrYearInt, startOrEndInt);
    setDRDState(newDRD);
  };

  const onSetPeriod = () => {
    const modVarObjListClone = structuredClone(modVarObjList);

    const timePeriodObjs = {
      [pip.dateRangeDisplayObj]: structuredClone(drdState),
    };

    pieh.onSetTimePeriod(
      timePeriodObjs,
      undefined,
      false,
      modVarObjListClone,
      props.origModVarObjArr,
      onModVarsChange,
      props.onCalculatingChange,
      props.onDialogChange
    );
  };

  return (
    <React.Fragment>
      {/* PEPFAR Download (temporarily disabled: https://trello.com/c/wkozEI43/459-for-the-disaggregate-targets-please-comment-out-the-download-pepfar-targets-button ) */}
      {false && (
        <div
          style={{
            marginTop: Theme.topIndent,
          }}
        >
          <div
            style={{
              marginBottom: Theme.ctrlSpacing,
            }}
          >
            {RS(SC.GB_stDownloadPepfarTargetsInstructions)
              .replace("FY24", `FY${currentFinancialYear % 100}`)
              .replaceAll("2024", currentFinancialYear.toString())
              .replace("2023", (currentFinancialYear - 1).toString())}
          </div>

          {!isAggMode && (
            // PEPFAR Download button in non-Aggregate mode
            <TButton
              caption={RS(SC.GB_stDownloadPepfarTargets)
                .replace("FY24", `FY${currentFinancialYear % 100}`)
                .replace("2024", currentFinancialYear.toString())}
              containerStyle={{
                display: "block",
              }}
              key={"downloadTargOutPEPFARBtn"}
              onClick={() => {
                downloadTargetsOutputPEPFARXLSX(modVarObjList);
              }}
              style={{
                backgroundColor: Theme.PI_TertiaryColor,
                width: 400,
              }}
              disabled={!includesFinancialYear}
            />
          )}

          {isAggMode && (
            // PEPFAR Download buttons in Aggregate mode
            <>
              <TButton
                caption={RS(SC.GB_stDownloadAggregatePepfarTargets)
                  .replace("FY24", `FY${currentFinancialYear % 100}`)
                  .replace("2024", currentFinancialYear.toString())}
                containerStyle={{
                  display: "block",
                }}
                key={"downloadTargOutPEPFARBtn"}
                onClick={() => {
                  downloadTargetsOutputPEPFARXLSX(modVarObjList, undefined);
                }}
                style={{
                  backgroundColor: Theme.PI_TertiaryColor,
                  width: 400,
                }}
                disabled={!includesFinancialYear}
              />

              <PIPepfarSessionDownload
                modVarObjList={modVarObjList}
                aggregateFilenames={aggregateFilenames}
                currentFinancialYear={currentFinancialYear}
                disabled={!includesFinancialYear}
              ></PIPepfarSessionDownload>
            </>
          )}
        </div>
      )}

      {/* Dates */}
      <PITimePeriodLab
        {...{
          style: { marginTop: Theme.topIndent },
          [pip.timePeriodObj]: targSettingPeriodObj,
          [pip.timePeriodType]: pic.targSetPeriod,
          [PITimePeriodLabProps]: { display: "block" },
        }}
      />
      {isAggMode && (
        <div>
          <PITimePeriodLab
            {...{
              [pip.timePeriodObj]: dateRangeDisplayObj,
              [pip.timePeriodType]: pic.dateRangeDisplayPeriod,
              [PITimePeriodLabProps]: { display: "block" },
            }}
          />
        </div>
      )}
      {!isAggMode && (
        <PITimePeriodDiv
          {...{
            [pip.boundingTimePeriodObj]: piasu.getTargSettingPeriodObj(modVarObjList),

            [pip.onSetPeriod]: onSetPeriod,

            [PITimePeriodDivProps.style]: {
              marginTop: Theme.topIndent,
            },

            [pip.onTimePeriodChange]: onDateRangeDisplayChange,

            [pip.timePeriodCaption]: RS(SC.GB_stDateRangeForDisplay),

            [pip.timePeriodObj]: drdState,
            [pip.timePeriodObjAppState]: dateRangeDisplayObj,

            [pip.timePeriodType]: pic.dateRangeDisplayPeriod,
          }}
        />
      )}
      {/* Method Selection */}
      {methods.length > 1 && (
        <PIMethodComboBox
          {...{
            outerStyle: {
              marginTop: Theme.topIndent,
            },
            [pias.modVarObjList]: modVarObjList,
            [pias.onModVarsChange]: onModVarsChange,

            [PIMethodComboBoxProps.row]: false,
          }}
        />
      )}
      {/* PrEP Status Selection */}
      <div
        style={{
          marginTop: 20,
        }}
      >
        <p
          style={{
            ...Theme.labelStyle,
            margin: 0,
            marginRight: Theme.leftIndent,
          }}
        >
          {RS(SC.GB_stDisplay)}
        </p>

        <TComboBox
          onChange={onPrEPStatusComboBoxChange}
          info={[pisc.PrEP_NEW_StatusMstID, pisc.PrEP_CT_StatusMstID, pisc.PrEP_Recipients_StatusMstID]}
          itemIndex={pisc.PrEP_Status_Options.indexOf(PrEPStatus)}
          items={[RS(SC.GB_stPrEPNEW), RS(SC.GB_stPrEPCT), RS(SC.GB_stPrEPRecipients)]}
          outerStyle={{
            marginTop: Theme.ctrlSpacing,
            fontFamily: Theme.fontFamily,
          }}
          style={{
            fontFamily: Theme.fontFamily,
          }}
        />
      </div>
      {/* Data Table */}

      {isAggMode && (
        <>
          <p
            key={"targDisagPriorPopsLab"}
            style={{
              marginTop: 30,
              padding: 0,
              ...Theme.labelStyle,
            }}
          >
            {RS(SC.GB_stTargetsBySessionAndPriorityPop)}
          </p>
          <PITargDisagDistrictPriorPopsAggResTable modVarObjList={modVarObjList} />
        </>
      )}
      {showDisagTable && (
        <>
          <p
            key={"targDisagDistrictPriorPopsLab"}
            style={{
              marginTop: Theme.topIndent,
              padding: 0,
              ...Theme.labelStyle,
            }}
          >
            {RS(SC.GB_stTargsDisagBy) + " " + area2Name.toLowerCase().trim() + ", " + RS(SC.GB_stByPriorPop)}
          </p>
          {!isAggMode && level1Name === "" && (
            <p
              style={{
                display: "block",
                marginTop: Theme.topIndent,
                ...Theme.textFontStyle,
              }}
            >
              {RS(SC.GB_stSpecifySubnatLevelOneToSeeTable)}
            </p>
          )}
          <PITargDisagDistrictPriorPopsResTable modVarObjList={modVarObjList} />
        </>
      )}
    </React.Fragment>
  );
};

PIDisagTargetsResultContent.propTypes = {
  modVarObjList: PropTypes.arrayOf(PropTypes.object).isRequired,
  origModVarObjArr: PropTypes.arrayOf(PropTypes.object),
  onModVarsChange: PropTypes.func,
  onCalculatingChange: PropTypes.func,
  onDialogChange: PropTypes.func,
};

PIDisagTargetsResultContent.defaultProps = {
  origModVarObjArr: [],
  onModVarsChange: () => {},
  onCalculatingChange: () => {},
  onDialogChange: () => {},
};

export default PIDisagTargetsResultContent;
